import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { FileUpload, resolveMediaUrl } from "@/components/FileUpload";
import { useUploadQueue } from "@/context/UploadQueueContext";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { prepareStoryMediaUrl, trimPayload } from "@/lib/storyPublish";
import { VideoTrimTrack } from "@/components/story/VideoTrimTrack";
import { UploadOverlay } from "@/components/PostComposer";
import { Switch } from "@/components/ui/switch";
import { BG_GRADIENTS, BG_IMAGES, gradientCss } from "@/lib/gradients";
import { STORY_FONTS, DEFAULT_STORY_FONT, storyFontStyle, storyFontScale, ensureAllStoryFonts, useStoryFontReady } from "@/lib/storyFonts";
import { AutoFitText } from "@/components/AutoFitText";
import { EmojiTabsGrid } from "@/components/EmojiTabsGrid";
import { CHRISTIAN_EMOJIS, STICKER_COLORS, TEXT_BADGES, mediaTransformStyle, StickerBody, stickerWrapStyle, StoryDrawing, StoryBlurBackdrop, storyBlurSrc, attachLocalPoster, revokeMediaUrls } from "@/components/StoryMedia";
import { MusicPicker } from "@/components/MusicPicker";
import { parseVideoEmbed, parseFirstUrl } from "@/lib/embeds";
import { stripEmbedUrl } from "@/lib/linkText";
import { LinkPreviewCard } from "@/components/LinkPreviewCard";
import { MediaFxEditor, keepDialogOpenForFx, FxButton } from "@/components/media/MediaFxEditor";
import { hasFx } from "@/lib/mediaFx";
import { Image as ImageIcon, Type, ChevronLeft, Church, ZoomIn, ZoomOut, Trash2, Smile, Music, AtSign, CalendarClock, Play, X, Search, Volume2 } from "lucide-react";
import { useIsMobile } from "@/lib/useIsMobile";
import { phoneNav, isNativeApp } from "@/lib/platform";
import { PC_COMPOSER_MODAL } from "@/lib/dialogSizes";
import { StoryFullscreenEditor } from "@/components/story/StoryFullscreenEditor";
import { imageVariant } from "@/lib/mediaUrl";

// Text stories start at 60% of the auto-fit size; the user enlarges with the slider.
import { DEFAULT_BG_TEXT_SCALE as DEFAULT_TEXT_SCALE } from "@/lib/useAutoFitText";
import { maxDatetimeLocal } from "@/lib/scheduleLimits";
import { AUDIENCES, audienceOf, audienceAccent, readDefaultStoryAudience, writeDefaultStoryAudience } from "@/lib/postAudience";
import { CloseFriendsLink, CloseFriendsDialog } from "@/components/CloseFriends";
import { StoryHiddenFromLink, StoryHiddenFromDialog } from "@/components/story/StoryHiddenFrom";

const IDENTITY_TF = { scale: 1, x: 0, y: 0 };
const clampOffset = (x, y, scale) => {
  const m = (scale - 1) * 50;
  return { x: Math.max(-m, Math.min(m, x)), y: Math.max(-m, Math.min(m, y)) };
};

const DRAFT_KEY = "yalola.story_draft";

const isoToLocalInput = (iso) => {
  try { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ""; }
};

const fmtTime = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
};

// Muted looping preview that plays ONLY the chosen extract window [start, start+len],
// so the user sees exactly what will be published (mini-lecture de l'extrait).
const ClipLoop = ({ src, start, len, className, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const seekStart = () => { try { v.currentTime = start; } catch { /* noop */ } };
    const onMeta = () => { seekStart(); v.play().catch(() => {}); };
    const onTime = () => { if (v.currentTime >= start + len || v.currentTime < start - 0.05) seekStart(); };
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    if (v.readyState >= 1) onMeta();
    return () => { v.removeEventListener("loadedmetadata", onMeta); v.removeEventListener("timeupdate", onTime); };
  }, [src, start, len]);
  return <video ref={ref} src={src} poster={NEUTRAL_POSTER} muted playsInline autoPlay className={className} data-testid={testId} />;
};


// Facebook-style story creator: photo/video story OR text story with gradients.
// Téléphone (web mobile < 768 px) et app native → éditeur plein écran façon Instagram (création
// et édition d'une annonce programmée) ; PC / tablette web → cette feuille à deux colonnes.
export const StoryComposer = (props) => {
  const phoneWeb = useIsMobile("(max-width: 767px)");
  if (phoneWeb || phoneNav()) return <StoryFullscreenEditor {...props} />;
  return <StoryComposerSheet {...props} />;
};

const StoryComposerSheet = ({ open, onOpenChange, onCreated, initialChurchId, initialAsChurch = false, editStory = null }) => {
  const { t, config, user } = useApp();
  const { enqueueStory } = useUploadQueue();
  const [mode, setMode] = useState(null); // null | media | text
  // media: {file, localUrl, url, media_type, progress, status: uploading|done|error}
  const [media, setMedia] = useState(null);
  const [text, setText] = useState("");
  const [bg, setBg] = useState(BG_GRADIENTS[0].key);
  const [textScale, setTextScale] = useState(DEFAULT_TEXT_SCALE); // slider around the auto-fit size (0.6–1.4)
  const [font, setFont] = useState(DEFAULT_STORY_FONT); // WhatsApp-style text font
  const fontReady = useStoryFontReady(font);
  useEffect(() => { if (mode === "text") ensureAllStoryFonts(); }, [mode]);
  const [busy, setBusy] = useState(false);
  const [proc, setProc] = useState(null); // client-side video prep: {phase:"trim"|"upload", pct:0..100}
  const [clipQuality, setClipQuality] = useState("standard"); // "light" | "standard" | "hd"
  // Video trim (long videos: pick an extract instead of rejecting ; lot 613 : début + fin, même sous la limite)
  const [videoDur, setVideoDur] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimLenState, setTrimLenState] = useState(null);
  const videoRef = useRef(null);
  const maxSec = Number(config?.settings?.story_video_max_seconds) || 30;
  const storyVideoMaxMb = Number(config?.settings?.media_limits?.story_video_max_mb) || Number(config?.settings?.media_limits?.video_max_mb) || 100;
  const trimLen = trimLenState ?? Math.min(maxSec, videoDur || maxSec);
  const setTrim = ({ start, len }) => { setTrimStart(start); setTrimLenState(len); };
  const canTrim = media?.media_type === "video" && !!media?.file && videoDur >= 2;
  const needsTrim = media?.media_type === "video" && videoDur > maxSec + 0.5;
  const willTrim = canTrim && (needsTrim || trimStart > 0.05 || trimLen < videoDur - 0.3);
  const willProcess = media?.media_type === "video" && (willTrim || (!!media?.file && media.file.size > storyVideoMaxMb * 1024 * 1024));
  const trimOpts = { videoDur, maxSec, storyVideoMaxMb, needsTrim: willTrim, trimStart, trimLen, clipQuality };
  const clipStart = willTrim ? trimStart : 0;
  const clipEnd = willTrim ? Math.min(videoDur, trimStart + trimLen) : Math.min(videoDur || maxSec, maxSec);

  // Media adjust (pan/zoom, non-destructive) + emoji stickers (Facebook/IG style).
  const [transform, setTransform] = useState(IDENTITY_TF);
  // Lot 534 — éditeur Filtres / Réglages / Recadrer (photo → fichier cuit ; vidéo → media_transform.fx en CSS).
  const [fxOpen, setFxOpen] = useState(false);
  const applyFx = (res) => {
    setFxOpen(false);
    if (!res) return;
    if (res.file) {
      setMedia((m) => { revokeMediaUrls(m); return { file: res.file, localUrl: URL.createObjectURL(res.file), url: null, media_type: "image", progress: 100, status: "done" }; });
      setTransform(IDENTITY_TF);
    } else if ("fx" in res) {
      setTransform((tf) => { const { fx: _old, ...rest } = tf; return res.fx ? { ...rest, fx: res.fx } : rest; });
    }
  };
  const [stickers, setStickers] = useState([]);
  const [selSticker, setSelSticker] = useState(null);
  const [emojiInput, setEmojiInput] = useState("");
  const [captionInput, setCaptionInput] = useState("");
  const [captionColor, setCaptionColor] = useState("#FFFFFF");
  const frameRef = useRef(null);
  const panRef = useRef(null);
  const stickerDragRef = useRef(null);
  const selScale = stickers.find((s) => s.id === selSticker)?.scale ?? 0.22;

  // Music overlay (admin-managed library or uploaded MP3, via shared MusicPicker), @mentions, scheduling.
  const [music, setMusic] = useState(null); // {url, title, start}
  const [musicVolume, setMusicVolume] = useState(0.7); // 0.35 soft · 0.7 medium · 1.0 loud
  const [mentions, setMentions] = useState([]); // [{id, name}]
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  // WhatsApp-style link preview: detect a URL in the text (text story) or in the text bubbles (photo/video
  // story), fetch its Open-Graph card, show it in the composer and attach it to the story (embed).
  const [linkEmbed, setLinkEmbed] = useState(null);
  const dismissedLinkRef = useRef(null);
  const fetchedLinkRef = useRef(null);
  const linkSourceText = mode === "text" ? text : stickers.filter((s) => s.type === "text").map((s) => s.text || "").join("\n");
  useEffect(() => {
    const video = parseVideoEmbed(linkSourceText);
    const rawUrl = video?.url || parseFirstUrl(linkSourceText);
    if (!rawUrl) { setLinkEmbed(null); fetchedLinkRef.current = null; return; }
    if (dismissedLinkRef.current === rawUrl || fetchedLinkRef.current === rawUrl) return;
    const timer = setTimeout(async () => {
      fetchedLinkRef.current = rawUrl;
      try {
        const { data } = await api.get("/link-preview", { params: { url: rawUrl } });
        setLinkEmbed(data.preview);
      } catch {
        setLinkEmbed(video ? { ...video, title: null, author: null, thumbnail: null } : null);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkSourceText, mode]);
  const removeLinkEmbed = () => {
    dismissedLinkRef.current = linkEmbed?.url || parseVideoEmbed(linkSourceText)?.url || parseFirstUrl(linkSourceText) || null;
    setLinkEmbed(null);
  };

  // Official church story (verified churches only; admins/managers or the matching preacher).
  const [churchElig, setChurchElig] = useState({ can: false, mode: null, churches: [] });
  const [asChurch, setAsChurch] = useState(false);
  const [churchId, setChurchId] = useState("");
  // Preacher: mandatory "Publish as" selector (mirror of the post composer), gated by admin feature flag.
  const _feat = config?.settings?.features || {};
  const _fEnabled = (k) => _feat[k] !== false;
  const publishAsEnabled = _fEnabled("preacher_publish_as");
  const rememberDest = _fEnabled("publish_as_memory");
  const memKey = user ? `yalola.publish_as.${user.id}` : null;
  const mandatoryPublishAs = publishAsEnabled && churchElig.mode === "preacher" && (churchElig.churches?.length || 0) > 0;
  const [storyPublishAs, setStoryPublishAs] = useState(null); // null | "me" | churchId
  // Lot 651 — audience de la story (mémoire séparée des publications) ; sans objet pour une story d'église.
  const [audience, setAudienceState] = useState(() => readDefaultStoryAudience(user));
  const setAudience = (v) => { setAudienceState(v); writeDefaultStoryAudience(v); };
  const audienceEnabled = !editStory && (mandatoryPublishAs ? (!storyPublishAs || storyPublishAs === "me") : !(asChurch && churchId));
  const [hiddenOpen, setHiddenOpen] = useState(false); // lot 652
  const [closeFriendsOpen, setCloseFriendsOpen] = useState(false); // lot 655
  useEffect(() => {
    if (!open) return;
    api.get("/stories/church-eligibility")
      .then(({ data }) => {
        const elig = data || { can: false, churches: [] };
        setChurchElig(elig);
        const list = elig.churches || [];
        if (initialChurchId && list.some((c) => c.id === initialChurchId)) {
          setChurchId(initialChurchId);
          if (initialAsChurch) { setAsChurch(true); setStoryPublishAs(initialChurchId); }
        } else if (list.length) {
          setChurchId((prev) => prev || list[0].id);
        }
      })
      .catch(() => setChurchElig({ can: false, mode: null, churches: [] }));
  }, [open, initialChurchId, initialAsChurch]);
  // Preselect the preacher's last destination to speed up publishing.
  useEffect(() => {
    if (!mandatoryPublishAs || !rememberDest || storyPublishAs || !memKey) return;
    try {
      const saved = localStorage.getItem(memKey);
      if (saved === "me" || churchElig.churches.some((c) => c.id === saved)) setStoryPublishAs(saved);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandatoryPublishAs, rememberDest, churchElig, memKey, storyPublishAs]);

  // Edit an existing scheduled announcement: pre-fill from the story.
  useEffect(() => {
    if (!open || !editStory) return;
    const s = editStory;
    const isText = s.media_type === "text";
    setMode(isText ? "text" : "media");
    // Story « free » (composée sur téléphone) : le texte n'est que la somme des bulles → champ principal vide
    setText(s.layout === "free" ? "" : (s.text || ""));
    setBg(s.bg || BG_GRADIENTS[0].key);
    setTextScale(typeof s.text_scale === "number" ? s.text_scale : 1);
    setFont(s.font || DEFAULT_STORY_FONT);
    if (isText) setMedia(null);
    else if (s.media_url) setMedia({ file: null, localUrl: null, url: s.media_url, media_type: s.media_type, progress: 100, status: "done" });
    setTransform(s.media_transform || IDENTITY_TF);
    setStickers(Array.isArray(s.stickers) ? s.stickers : []);
    if (s.scheduled_at) { setScheduleOn(true); setScheduledAt(isoToLocalInput(s.scheduled_at)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editStory]);

  // Draft: restore on mount.
  useEffect(() => {
    if (editStory) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.mode) setMode(d.mode);
      if (d.text) setText(d.text);
      if (d.bg) setBg(d.bg);
      if (d.font) setFont(d.font);
      if (d.trim_start) setTrimStart(d.trim_start);
      if (d.media?.url) {
        setMedia({ file: null, localUrl: null, url: d.media.url, media_type: d.media.media_type, progress: 100, status: "done" });
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft: persist on change (only finished uploads).
  useEffect(() => {
    if (editStory) return;
    const m = media?.status === "done" && media.url ? { url: media.url, media_type: media.media_type } : null;
    if (!text.trim() && !m) { localStorage.removeItem(DRAFT_KEY); return; }
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ mode, text, bg, font, media: m, trim_start: trimStart || 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, text, bg, font, media, trimStart]);

  // Keep the preview inside the selected extract.
  useEffect(() => {
    if (willTrim && videoRef.current) {
      try { videoRef.current.currentTime = trimStart; } catch { /* noop */ }
    }
  }, [trimStart, trimLen, willTrim]);

  const hasDraft = !!text.trim() || !!media;

  const clearMedia = () => {
    setMedia((m) => { revokeMediaUrls(m); return null; });
    setVideoDur(0); setTrimStart(0); setTrimLenState(null); setProc(null); setClipQuality("standard");
    setTransform(IDENTITY_TF); setStickers([]); setSelSticker(null);
  };
  const reset = () => {
    setMode(null); clearMedia(); setText(""); setBg(BG_GRADIENTS[0].key); setTextScale(DEFAULT_TEXT_SCALE); setFont(DEFAULT_STORY_FONT);
    setMusic(null); setMusicVolume(0.7); setMentions([]); setMentionQuery(""); setMentionResults([]);
    setScheduleOn(false); setScheduledAt("");
    setStoryPublishAs(null);
    localStorage.removeItem(DRAFT_KEY);
  };
  // Accidental close (X / Escape / outside) keeps the draft; "Abandonner" discards it.
  const close = (o) => {
    if (!o && hasDraft) toast.info(t("composer.draft_kept", "Brouillon conservé — vous pourrez reprendre plus tard"), { duration: 2500 });
    if (!o && !hasDraft) reset();
    onOpenChange(o);
  };
  const discard = () => { reset(); onOpenChange(false); };

  // Instant local preview only. Nothing is uploaded until the user taps
  // "Partager": then the whole job (trim + upload + create) runs in the
  // BACKGROUND queue (Facebook-style) and the composer closes immediately.
  const handleFiles = (files) => {
    const f = files && files[0];
    if (!f) return;
    clearMedia();
    const isVideo = f.type.startsWith("video");
    const localUrl = URL.createObjectURL(f);
    const att = {
      file: f, localUrl, url: null,
      media_type: isVideo ? "video" : "image",
      progress: 0, status: isVideo ? "local" : "done",
    };
    setMedia(att);
    if (isVideo) attachLocalPoster(localUrl, f, setMedia);
  };

  const onPreviewMeta = (e) => {
    const d = e.currentTarget.duration;
    if (d && !isNaN(d) && Math.abs(d - videoDur) > 0.05) { setVideoDur(d); setTrimStart(0); setTrimLenState(null); }
  };

  // Loop the preview inside the selected window.
  const onPreviewTime = (e) => {
    if (!willTrim) return;
    const v = e.currentTarget;
    if (v.currentTime > trimStart + trimLen || v.currentTime < trimStart - 0.5) {
      try { v.currentTime = trimStart; } catch { /* noop */ }
    }
  };

  // Media pan (drag) + zoom (slider).
  const onMediaPointerDown = (e) => {
    if (e.target?.dataset?.sticker) return;
    // Don't hijack (pointer-capture) a press that starts on a control like the
    // "Retirer" button — capture would steal the pointerup and cancel its click.
    if (e.target?.closest?.("button")) return;
    setSelSticker(null);
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    panRef.current = { px: e.clientX, py: e.clientY, ox: transform.x, oy: transform.y, w: rect.width, h: rect.height };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onMediaPointerMove = (e) => {
    if (!panRef.current) return;
    const p = panRef.current;
    const dx = ((e.clientX - p.px) / p.w) * 100;
    const dy = ((e.clientY - p.py) / p.h) * 100;
    const { x, y } = clampOffset(p.ox + dx, p.oy + dy, transform.scale);
    setTransform((tf) => ({ ...tf, x, y }));
  };
  const onMediaPointerUp = () => { panRef.current = null; };
  const onZoom = (v) => setTransform((tf) => { const { x, y } = clampOffset(tf.x, tf.y, v); return { scale: v, x, y }; });

  // Stickers: add / drag / resize / delete.
  const stagger = () => { const n = stickers.length % 6; return { x: 0.42 + n * 0.03, y: 0.4 + n * 0.05 }; };
  const addSticker = (emoji) => {
    const e = (emoji || "").trim();
    if (!e) return;
    const id = Math.random().toString(36).slice(2, 9);
    const { x, y } = stagger();
    setStickers((arr) => [...arr, { id, type: "emoji", emoji: e, x, y, scale: 0.24, rotation: 0 }].slice(-20));
    setSelSticker(id);
  };
  const addTextSticker = (text, color = "#FFFFFF", bg = "") => {
    const v = (text || "").trim();
    if (!v) return;
    const id = Math.random().toString(36).slice(2, 9);
    const { x, y } = stagger();
    setStickers((arr) => [...arr, { id, type: "text", text: v, color, bg, x, y, scale: 0.16, rotation: 0 }].slice(-20));
    setSelSticker(id);
  };
  const recolorSel = (c) => setStickers((arr) => arr.map((s) => (s.id === selSticker && s.type === "text" ? { ...s, color: c } : s)));
  const onStickerPointerDown = (e, id) => {
    e.stopPropagation();
    setSelSticker(id);
    const rect = frameRef.current?.getBoundingClientRect();
    const st = stickers.find((s) => s.id === id);
    if (!rect || !st) return;
    stickerDragRef.current = { id, px: e.clientX, py: e.clientY, ox: st.x, oy: st.y, w: rect.width, h: rect.height };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onStickerPointerMove = (e) => {
    const d = stickerDragRef.current;
    if (!d) return;
    const nx = Math.max(0.03, Math.min(0.97, d.ox + (e.clientX - d.px) / d.w));
    const ny = Math.max(0.03, Math.min(0.97, d.oy + (e.clientY - d.py) / d.h));
    setStickers((arr) => arr.map((s) => (s.id === d.id ? { ...s, x: nx, y: ny } : s)));
  };
  const onStickerPointerUp = () => { stickerDragRef.current = null; };
  const resizeSel = (v) => setStickers((arr) => arr.map((s) => (s.id === selSticker ? { ...s, scale: v } : s)));
  const deleteSticker = (id) => { setStickers((arr) => arr.filter((s) => s.id !== id)); setSelSticker((cur) => (cur === id ? null : cur)); };
  const deleteSel = () => { if (selSticker) deleteSticker(selSticker); };

  // Editable overlay of stickers (emoji + text), shared by media & text modes.
  // The selected sticker carries a touch-friendly « × » so it can be removed right on the
  // preview (no need to find the tools further down the sheet).
  const renderStickerLayer = () => (
    <div className="absolute inset-0" style={{ containerType: "inline-size" }}>
      {stickers.map((s) => (
        <span
          key={s.id}
          data-sticker="1"
          onPointerDown={(e) => onStickerPointerDown(e, s.id)}
          onPointerMove={onStickerPointerMove}
          onPointerUp={onStickerPointerUp}
          className={`absolute cursor-move select-none ${selSticker === s.id ? "outline outline-2 outline-white/80 rounded" : ""}`}
          style={{ ...stickerWrapStyle(s), touchAction: "none" }}
          data-testid={`story-sticker-${s.id}`}
          data-type={s.type}
        >
          {/* Rendu partagé avec la visionneuse et l'éditeur mobile (emoji, bulle, verset, heure, lieu, mention, carte de lien) ;
              une bulle porte l'URL de la carte → l'URL est masquée comme dans la visionneuse (bulle vide → URL laissée visible pour pouvoir la retirer). */}
          <StickerBody s={s.type === "text" && linkEmbed ? { ...s, text: stripEmbedUrl(s.text, linkEmbed) || s.text } : s} embed={linkEmbed} />
          {selSticker === s.id && (
            <button
              type="button"
              aria-label={t("common.delete", "Supprimer")}
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => { e.stopPropagation(); deleteSticker(s.id); }}
              className="absolute grid place-items-center rounded-full bg-black/80 text-white shadow"
              style={{ top: "-10px", right: "-10px", width: "22px", height: "22px", fontSize: "12px", lineHeight: 1, filter: "none", touchAction: "manipulation" }}
              data-testid={`story-sticker-remove-${s.id}`}
            >
              <X size={13} />
            </button>
          )}
        </span>
      ))}
    </div>
  );

  // Size + delete of the selected sticker — rendered right under the (sticky) preview so
  // it is always visible, on the phone as on PC.
  const renderStickerControls = () => selSticker ? (
    <div className="mx-auto w-full max-w-[340px] flex items-center gap-2 mt-2" data-testid="story-sticker-controls">
      <span className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{t("story.sticker_size", "Taille")}</span>
      <Slider value={[selScale]} min={0.08} max={0.5} step={0.01} onValueChange={([v]) => resizeSel(v)} className="flex-1" data-testid="story-sticker-size" />
      <button type="button" onClick={deleteSel} className="h-8 px-2.5 flex items-center gap-1 rounded-full text-xs font-semibold hover:bg-[color:var(--yalola-surface-alt)] text-red-600" data-testid="story-sticker-delete">
        <Trash2 size={15} /> {t("common.delete", "Supprimer")}
      </button>
    </div>
  ) : null;

  // Phone: the story being built stays pinned at the top of the sheet (compact 9:16) while
  // the tools scroll underneath — what you add (emoji, text, background) is always visible.
  // PC: same shell keeps the preview in view in its column.
  const previewShell = (frame) => (
    <div
      className="sticky top-0 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 md:mx-0 md:px-0 -mt-1 pt-2 pb-2 md:self-start"
      style={{ background: "var(--yalola-surface)", boxShadow: "0 10px 14px -12px rgba(0,0,0,0.35)" }}
      data-testid="story-preview-shell"
    >
      {frame}
      {renderStickerControls()}
    </div>
  );
  // Phone / native: compact 9:16 pinned on top. PC (web): height capped to the window so the tools column
  // beside it stays visible on small screens (no hidden "scroll to find the emojis").
  const PREVIEW_FRAME = "mx-auto aspect-[9/16] h-[min(36dvh,320px)] w-auto max-w-full md:h-[min(58dvh,462px)]";
  // Web PC: preview left, tools right (same as the text story). Native tablets keep the phone layout.
  const PC_COLUMNS = isNativeApp() ? "" : "md:grid md:grid-cols-2 md:gap-4 md:items-start";

  const renderStickerTools = () => (
    <div className="space-y-2.5" data-testid="story-sticker-tools">
      <div>
        <div className="text-xs font-semibold flex items-center gap-1 mb-1" style={{ color: "var(--yalola-text-muted)" }}>
          <Smile size={13} /> {t("story.add_sticker", "Ajouter un emoji")}
        </div>
        <EmojiTabsGrid cols={8} cell="h-9 w-full text-2xl" onPick={addSticker} gridTestId="story-sticker-palette" itemTestId={(e) => `story-sticker-add-${e}`} />
        <div className="text-[11px] font-semibold flex items-center gap-1 mt-1.5 mb-0.5" style={{ color: "var(--yalola-primary)" }}>
          ✝️ {t("story.christian_pack", "Chrétien")}
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar py-1" data-testid="story-christian-palette">
          {CHRISTIAN_EMOJIS.map((e) => (
            <button key={e} type="button" onClick={() => addSticker(e)} className="text-2xl shrink-0 transition-transform hover:scale-125" data-testid={`story-christian-add-${e}`}>{e}</button>
          ))}
        </div>
        <div className="flex gap-2 mt-1">
          <input
            value={emojiInput}
            onChange={(ev) => setEmojiInput(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addSticker(emojiInput); setEmojiInput(""); } }}
            maxLength={8}
            placeholder={t("story.sticker_input", "Tapez un emoji…")}
            className="flex-1 min-w-0 rounded-full border px-3 py-1.5 text-sm outline-none"
            style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
            data-testid="story-sticker-input"
          />
          <Button type="button" variant="outline" className="rounded-full" onClick={() => { addSticker(emojiInput); setEmojiInput(""); }} data-testid="story-sticker-add-custom">
            {t("common.add", "Ajouter")}
          </Button>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold flex items-center gap-1 mb-1" style={{ color: "var(--yalola-text-muted)" }}>
          <Type size={13} /> {t("story.add_text", "Ajouter du texte")}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="story-text-badges">
          {TEXT_BADGES.map((b, i) => (
            <button key={i} type="button" onClick={() => addTextSticker(t(b.key, b.text), b.color, b.bg)} className="text-[11px] font-bold px-2 py-1 rounded-full transition-transform hover:scale-105" style={{ color: b.color, background: b.bg || "var(--yalola-surface-alt)" }} data-testid={`story-text-badge-${i}`}>
              {t(b.key, b.text)}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={captionInput}
            onChange={(ev) => setCaptionInput(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addTextSticker(captionInput, captionColor); setCaptionInput(""); } }}
            maxLength={120}
            placeholder={t("story.text_input", "Écrivez un texte…")}
            className="flex-1 min-w-0 rounded-full border px-3 py-1.5 text-sm outline-none"
            style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
            data-testid="story-text-caption-input"
          />
          <Button type="button" variant="outline" className="rounded-full" onClick={() => { addTextSticker(captionInput, captionColor); setCaptionInput(""); }} data-testid="story-text-add">
            {t("common.add", "Ajouter")}
          </Button>
        </div>
        <div className="flex items-center gap-1.5 mt-1.5" data-testid="story-text-colors">
          {STICKER_COLORS.map((c) => (
            <button key={c} type="button" onClick={() => { setCaptionColor(c); recolorSel(c); }} className="h-6 w-6 rounded-full border transition-transform hover:scale-110" style={{ background: c, boxShadow: captionColor === c ? "0 0 0 2px white, 0 0 0 3.5px var(--yalola-primary)" : "none", borderColor: "var(--yalola-border)" }} data-testid={`story-text-color-${c}`} />
          ))}
        </div>
      </div>
    </div>
  );

  // --- Mentions / scheduling helpers ---
  useEffect(() => {
    const q = mentionQuery.trim();
    if (q.length < 2) { setMentionResults([]); return undefined; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setMentionResults((data.users || []).filter((u) => !mentions.some((m) => m.id === u.id)).slice(0, 5));
      } catch { setMentionResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [mentionQuery, mentions]);
  const addMention = (u) => { setMentions((arr) => (arr.some((m) => m.id === u.id) ? arr : [...arr, { id: u.id, name: u.name }].slice(0, 10))); setMentionQuery(""); setMentionResults([]); };
  const removeMention = (id) => setMentions((arr) => arr.filter((m) => m.id !== id));

  const minDatetimeLocal = () => {
    const d = new Date(Date.now() + 5 * 60000);
    const l = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return l.toISOString().slice(0, 16);
  };

  const renderExtras = () => (
    <div className="space-y-3 border-t pt-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="story-extras">
      {/* Music: shared picker (admin library + trending + featured + extract) */}
      <div>
        <div className="text-xs font-semibold flex items-center gap-1 mb-1.5" style={{ color: "var(--yalola-text-muted)" }}>
          <Music size={13} /> {t("story.music", "Musique")}
        </div>
        <MusicPicker music={music} onChange={setMusic} previewVolume={musicVolume} t={t} testId="story-music" />
        {music && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium flex items-center gap-1.5 shrink-0">
              <Volume2 size={14} style={{ color: "var(--yalola-primary)" }} />
              {t("composer.music_volume", "Volume de la musique")}
            </span>
            <div className="flex gap-1" data-testid="story-music-volume">
              {[["soft", 0.35, "Doux"], ["medium", 0.7, "Moyen"], ["loud", 1.0, "Fort"]].map(([key, val, label]) => {
                const active = Math.abs(musicVolume - val) < 0.01;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMusicVolume(val)}
                    className="rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors"
                    style={{ borderColor: active ? "var(--yalola-primary)" : "var(--yalola-border)", background: active ? "var(--yalola-primary)" : "transparent", color: active ? "#fff" : "inherit" }}
                    data-testid={`story-music-vol-${key}`}
                  >
                    {t(`composer.music_vol_${key}`, label)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* @Mentions */}
      <div>
        <div className="text-xs font-semibold flex items-center gap-1 mb-1.5" style={{ color: "var(--yalola-text-muted)" }}>
          <AtSign size={13} /> {t("story.mention", "Mentionner des membres")}
        </div>
        {mentions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="story-mention-chips">
            {mentions.map((m) => (
              <span key={m.id} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-primary)" }} data-testid={`story-mention-chip-${m.id}`}>
                @{m.name}<button type="button" onClick={() => removeMention(m.id)}><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
          <input value={mentionQuery} onChange={(e) => setMentionQuery(e.target.value)} placeholder={t("story.mention_search", "Rechercher un membre…")}
            className="w-full rounded-full border pl-9 pr-3 py-1.5 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="story-mention-input" />
          {mentionResults.length > 0 && (
            <div className="absolute z-30 mt-1 w-full rounded-xl border bg-white shadow-lg overflow-hidden" style={{ borderColor: "var(--yalola-border)" }} data-testid="story-mention-results">
              {mentionResults.map((u) => (
                <button key={u.id} type="button" onClick={() => addMention(u)} className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-[color:var(--yalola-surface-alt)] text-left" data-testid={`story-mention-result-${u.id}`}>
                  {u.avatar_url
                    ? <img src={imageVariant(u.avatar_url, 48)} alt="" className="h-6 w-6 rounded-full object-cover" />
                    : <span className="h-6 w-6 rounded-full grid place-items-center text-white text-[11px]" style={{ background: "var(--yalola-primary)" }}>{u.name?.[0]}</span>}
                  <span className="truncate">{u.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Schedule for later */}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
            <CalendarClock size={13} /> {t("story.schedule", "Programmer la publication")}
          </div>
          <Switch checked={scheduleOn} onCheckedChange={(v) => { setScheduleOn(v); if (!v) setScheduledAt(""); }} data-testid="story-schedule-switch" />
        </div>
        {scheduleOn && (
          <input type="datetime-local" value={scheduledAt} min={minDatetimeLocal()} max={maxDatetimeLocal()} onChange={(e) => setScheduledAt(e.target.value)}
            className="mt-1.5 w-full rounded-lg border px-3 py-1.5 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="story-schedule-input" />
        )}
      </div>
    </div>
  );

  // Résolution de l'URL du média (découpe + envoi) : logique partagée avec l'éditeur plein écran mobile.
  const prepareMediaUrl = () => prepareStoryMediaUrl({ media, config, t, ...trimOpts, setProc });

  const publishInline = async ({ church, extras }) => {
    setBusy(true);
    try {
      if (editStory) {
        if (!extras.scheduled_at) { toast.error(t("story.schedule_required", "Choisis une date et une heure futures.")); setBusy(false); return; }
        const cid = editStory.church_id || initialChurchId;
        const bubbles = stickers.filter((st) => st.type === "text").map((st) => st.text).join("\n").trim();
        const freeLayout = mode === "text" && editStory.layout === "free" && !text.trim();
        const body = { media_type: mode === "media" ? media.media_type : "text", text: text.trim() || (freeLayout ? bubbles : ""), bg, scheduled_at: extras.scheduled_at };
        // Le dessin (fait sur téléphone) n'est pas modifiable ici mais doit être conservé
        if (editStory.drawing_url) body.drawing_url = editStory.drawing_url;
        if (freeLayout) body.layout = "free";
        if (linkEmbed) body.embed = linkEmbed;
        if (mode === "text") { body.text_scale = textScale; body.font = font; }
        if (mode === "media") {
          const m = await prepareMediaUrl();
          body.media_url = m.url;
          if (m.poster) body.poster_url = m.poster;
          Object.assign(body, trimPayload(m));
          body.media_transform = transform;
          if (stickers.length) body.stickers = stickers;
        } else if (stickers.length) { body.stickers = stickers; }
        await api.put(`/churches/${cid}/scheduled-announcements/${editStory.id}`, body);
        toast.success(t("church.announcement_updated", "Annonce programmée mise à jour ✓"));
        reset();
        onOpenChange(false);
        return;
      }
      let res;
      if (mode === "media") {
        const m = await prepareMediaUrl();
        const payload = { media_url: m.url, media_type: media.media_type, ...(m.poster ? { poster_url: m.poster } : {}), ...trimPayload(m), ...church, ...extras };
        payload.media_transform = transform;
        if (stickers.length) payload.stickers = stickers;
        if (linkEmbed) payload.embed = linkEmbed;
        res = await api.post("/stories", payload);
      } else {
        if (!text.trim()) { toast.info(t("story.text_placeholder", "Commence à écrire...")); setBusy(false); return; }
        res = await api.post("/stories", { media_type: "text", text: text.trim(), bg, text_scale: textScale, font, ...(stickers.length ? { stickers } : {}), ...(linkEmbed ? { embed: linkEmbed } : {}), ...church, ...extras });
      }
      toast.success(res?.data?.scheduled
        ? t("story.scheduled_ok", "Story programmée ✓ — elle sera publiée automatiquement.")
        : t("story.created", "Story publiée !"));
      reset();
      onOpenChange(false);
      if (!res?.data?.scheduled) onCreated?.();
    } catch (e) { setProc(null); toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    const church = mandatoryPublishAs
      ? (storyPublishAs && storyPublishAs !== "me" ? { church_id: storyPublishAs } : {})
      : (asChurch && churchId ? { church_id: churchId } : {});
    if (mandatoryPublishAs && rememberDest && storyPublishAs && memKey) { try { localStorage.setItem(memKey, storyPublishAs); } catch { /* noop */ } }
    const extras = {};
    if (music) {
      extras.music_url = music.url;
      extras.music_title = music.title;
      extras.music_volume = musicVolume;
      if (music.start) extras.music_start = music.start;
    }
    if (mentions.length) extras.mentions = mentions;
    if (!church.church_id) extras.visibility = audience; // lot 651 — audience (story personnelle seulement)
    const scheduled = scheduleOn && !!scheduledAt;
    if (scheduled) { try { extras.scheduled_at = new Date(scheduledAt).toISOString(); } catch { /* noop */ } }

    // Editing an existing scheduled announcement, or scheduling a TEXT story → inline (nothing to upload).
    // Une story programmée AVEC média passe par la file d'arrière-plan (scheduled_at dans extras) : plus de bouton figé.
    if (editStory || (scheduled && mode !== "media")) { return publishInline({ church, extras }); }

    // Immediate text story → instant inline post (nothing to upload).
    if (mode !== "media") {
      if (!text.trim()) { toast.info(t("story.text_placeholder", "Commence à écrire...")); return; }
      setBusy(true);
      try {
        await api.post("/stories", { media_type: "text", text: text.trim(), bg, text_scale: textScale, font, ...(stickers.length ? { stickers } : {}), ...(linkEmbed ? { embed: linkEmbed } : {}), ...church, ...extras });
        toast.success(t("story.created", "Story publiée !"));
        reset();
        onOpenChange(false);
        onCreated?.();
      } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
      finally { setBusy(false); }
      return;
    }

    // Immediate media story → Facebook-style: hand off to the background queue,
    // close the composer instantly. The "Partage en cours…" tile shows progress.
    if (!media) return;
    enqueueStory({
      mode: "media",
      media: { file: media.file, url: media.url, media_type: media.media_type, poster_url: media.poster_url || null },
      text: text.trim(),
      bg,
      transform,
      stickers,
      church,
      extras,
      embed: linkEmbed || null,
      trimOpts,
      previewFile: media.file || null,
      mediaKind: media.media_type,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent variant="sheet" className={PC_COMPOSER_MODAL} data-testid="story-composer-modal" onInteractOutside={keepDialogOpenForFx}>
        {fxOpen && media && (
          <MediaFxEditor open media={{ kind: media.media_type, src: media.localUrl || resolveMediaUrl(media.url), file: media.file || null, poster: media.posterLocal || null }} initialFx={transform.fx} onApply={applyFx} onClose={() => setFxOpen(false)} t={t} />
        )}
        <DialogHeader>
          <DialogTitle className="text-center font-display flex items-center justify-center gap-2">
            {mode && (
              <button onClick={() => { setMode(null); clearMedia(); }} className="absolute left-3 top-[calc(0.6rem+var(--sa-top,0px))] sm:left-4 sm:top-4 h-8 w-8 -mt-2 sm:mt-0 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" data-testid="story-back-btn">
                <ChevronLeft size={18} />
              </button>
            )}
            {t("story.add", "Créer une story")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("story.add", "Créer une story")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">

        {/* Preacher: mandatory "Publish as" selector (mirror of the post composer) */}
        {mode && mandatoryPublishAs && (
          <div className="rounded-xl px-3 py-2.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="story-publish-as">
            <div className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
              <Church size={14} style={{ color: "var(--yalola-primary)" }} />
              {t("composer.publish_as", "Publier en tant que")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setStoryPublishAs("me")}
                aria-pressed={storyPublishAs === "me"}
                className="chip transition-colors"
                style={storyPublishAs === "me"
                  ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" }
                  : { background: "var(--yalola-surface)", color: "var(--yalola-text)" }}
                data-testid="story-publish-as-me"
              >
                {t("composer.publish_as_me", "Moi (Compte personnel)")}
              </button>
              {churchElig.churches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setStoryPublishAs(c.id)}
                  aria-pressed={storyPublishAs === c.id}
                  className="chip transition-colors flex items-center gap-1"
                  style={storyPublishAs === c.id
                    ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" }
                    : { background: "var(--yalola-surface)", color: "var(--yalola-text)" }}
                  data-testid={`story-publish-as-${c.id}`}
                >
                  <Church size={13} /> {c.name}
                </button>
              ))}
            </div>
            {!storyPublishAs && (
              <div className="text-[11px] mt-1.5" style={{ color: "var(--yalola-primary)" }} data-testid="story-publish-as-hint">
                {t("composer.publish_as_required", "Choisissez une destination pour publier.")}
              </div>
            )}
          </div>
        )}

        {/* Official church story toggle (verified churches) — optional path for admins/managers */}
        {mode && churchElig.can && !mandatoryPublishAs && (
          <div className="rounded-xl px-3 py-2.5 flex items-center gap-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="story-church-section">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <Church size={15} style={{ color: "var(--yalola-primary)" }} />
                {t("story.as_church", "Publier comme story d'église officielle")}
              </div>
              {asChurch && churchElig.churches.length > 1 ? (
                <select
                  value={churchId}
                  onChange={(e) => setChurchId(e.target.value)}
                  className="mt-1.5 w-full text-sm rounded-lg border px-2 py-1.5 outline-none"
                  style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
                  data-testid="story-church-select"
                >
                  {churchElig.churches.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ""}</option>
                  ))}
                </select>
              ) : asChurch && churchElig.churches[0] ? (
                <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="story-church-name">{churchElig.churches[0].name}</div>
              ) : null}
            </div>
            <Switch checked={asChurch} onCheckedChange={setAsChurch} data-testid="story-as-church-switch" />
          </div>
        )}

        {/* Lot 651 — audience de la story (PC) : mêmes chips que le composeur de publication */}
        {mode && audienceEnabled && (
          <div className="rounded-xl px-3 py-2.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="story-audience" data-audience={audience}>
            <div className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
              {React.createElement(audienceOf(audience).icon, { size: 14, style: { color: "var(--yalola-primary)" } })}
              {t("audience.story_title", "Qui peut voir ta story ?")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AUDIENCES.map((a) => (
                <button key={a.key} type="button" onClick={() => setAudience(a.key)} aria-pressed={audience === a.key} className="chip transition-colors flex items-center gap-1"
                  style={audience === a.key ? { background: audienceAccent(a.key), color: "#fff", borderColor: "transparent" } : { background: "var(--yalola-surface)", color: a.accent || "var(--yalola-text)" }}
                  title={t(a.dkey, a.desc)} data-testid={`story-audience-${a.key}`}>
                  <a.icon size={13} /> {t(a.tkey, a.label)}
                </button>
              ))}
            </div>
            {/* Lot 652 — « Masquer ma story à… » (liste du compte, toutes mes stories) */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {audience === "close_friends" && <CloseFriendsLink onClick={() => setCloseFriendsOpen(true)} testId="story-close-friends-link" />}
              {audience !== "private" && <StoryHiddenFromLink onClick={() => setHiddenOpen(true)} />}
            </div>
          </div>
        )}
        <StoryHiddenFromDialog open={hiddenOpen} onOpenChange={setHiddenOpen} />
        <CloseFriendsDialog open={closeFriendsOpen} onOpenChange={setCloseFriendsOpen} />

        {/* Type chooser — like Facebook */}
        {!mode && (
          <div className="grid grid-cols-2 gap-4 py-2" data-testid="story-mode-chooser">
            <FileUpload
              accept="image/*,video/*"
              onFiles={(files) => { setMode("media"); handleFiles(files); }}
              variant="custom"
              testId="story-mode-media"
              context="story"
              allowLargeVideo
              className="relative aspect-[3/4] rounded-2xl overflow-hidden text-white transition-transform hover:scale-[1.02] w-full"
            >
              <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,#3A7BD5,#7FC1FF)" }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                <span className="h-12 w-12 rounded-full bg-white grid place-items-center"><ImageIcon size={22} style={{ color: "#1E6FEB" }} /></span>
                <span className="text-sm font-semibold text-center leading-snug">{t("story.with_photo", "Créer une story avec photos")}</span>
                <span className="text-[11px] opacity-90 text-center">{t("story.media_hint", `Photo ou vidéo — l'extrait de ${maxSec}s est découpé automatiquement`).replace("{n}", maxSec)}</span>
              </div>
            </FileUpload>
            <button
              type="button"
              onClick={() => setMode("text")}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden text-white transition-transform hover:scale-[1.02]"
              data-testid="story-mode-text"
            >
              <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,#B24592,#F15F79)" }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                <span className="h-12 w-12 rounded-full bg-white grid place-items-center"><Type size={22} style={{ color: "#B24592" }} /></span>
                <span className="text-sm font-semibold text-center leading-snug">{t("story.with_text", "Créer une story avec du texte")}</span>
              </div>
            </button>
          </div>
        )}

        {/* Photo/video story */}
        {mode === "media" && (
          <div className="space-y-3">
            {!media ? (
              <FileUpload
                accept="image/*,video/*"
                onFiles={handleFiles}
                variant="custom"
                testId="story-media-upload"
                context="story"
                allowLargeVideo
                className="w-full rounded-2xl border-2 border-dashed p-10 grid place-items-center text-sm"
              >
                <span style={{ color: "var(--yalola-text-muted)" }}>{t("story.pick_media", "Choisis une photo ou une vidéo")}</span>
              </FileUpload>
            ) : (
              <div className={`flex flex-col gap-3 ${PC_COLUMNS}`} data-testid="story-media-layout">
              {previewShell(
              <div
                ref={frameRef}
                className={`${PREVIEW_FRAME} md:max-w-[260px] rounded-2xl overflow-hidden bg-black relative touch-none`}
                data-testid="story-media-preview"
                onPointerDown={onMediaPointerDown}
                onPointerMove={onMediaPointerMove}
                onPointerUp={onMediaPointerUp}
              >
                <StoryBlurBackdrop src={storyBlurSrc(media, media.localUrl || resolveMediaUrl(media.url))} testId="story-media-preview-blur" />
                {media.media_type === "video"
                  ? <video ref={videoRef} src={media.localUrl || resolveMediaUrl(media.url)} poster={NEUTRAL_POSTER} autoPlay muted loop playsInline onLoadedMetadata={onPreviewMeta} onTimeUpdate={onPreviewTime} className="relative w-full h-full object-contain pointer-events-none" style={mediaTransformStyle(transform)} />
                  : <img src={media.localUrl || resolveMediaUrl(media.url)} alt="" className="relative w-full h-full object-contain pointer-events-none" style={mediaTransformStyle(transform)} />}

                <StoryDrawing url={editStory?.drawing_url} />
                {/* Editable stickers (emoji + text) */}
                {renderStickerLayer()}

                <UploadOverlay att={media.status === "local" ? null : media} t={t} onRetry={() => {}} />
                {proc && (
                  <div className="absolute inset-0 z-20 bg-black/55 grid place-items-center" data-testid="story-proc-overlay">
                    <div className="w-2/3 max-w-[180px] text-center">
                      <div className="h-1.5 rounded-full bg-white/30 overflow-hidden">
                        <div className="h-full bg-white rounded-full transition-[width] duration-200" style={{ width: `${proc.pct}%` }} />
                      </div>
                      <div className="text-white text-[11px] mt-1.5 font-medium">
                        {(proc.phase === "trim" ? t("story.trimming", "Découpage") : t("story.uploading_clip", "Envoi")) + ` ${proc.pct}%`}
                      </div>
                    </div>
                  </div>
                )}
                <button onClick={clearMedia} className="absolute top-2 right-2 z-20 rounded-full bg-black/60 text-white px-2 py-1 text-xs" data-testid="story-media-remove">
                  {t("composer.remove", "Retirer")}
                </button>
              </div>
              )}

              {/* Tools column (PC) / stack under the pinned preview (phone): zoom, stickers, trim, clip, extras */}
              <div className="space-y-3 min-w-0" data-testid="story-media-tools">
              <div className="mx-auto w-full max-w-[340px] space-y-2.5" data-testid="story-adjust-tools">
                <div className="flex items-center gap-2">
                  <ZoomOut size={16} style={{ color: "var(--yalola-text-muted)" }} />
                  <Slider value={[transform.scale]} min={1} max={3} step={0.02} onValueChange={([v]) => onZoom(v)} className="flex-1" data-testid="story-zoom" />
                  <ZoomIn size={16} style={{ color: "var(--yalola-text-muted)" }} />
                </div>
                <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("story.adjust_hint", "Ta photo ou vidéo s'affiche en entier. Zoome ou fais-la glisser seulement si tu veux recadrer.")}</p>
                <FxButton onClick={() => setFxOpen(true)} active={hasFx(transform.fx)} className="!h-9 !px-3.5" testId="story-fx-btn" t={(k, d) => (k === "fx.button" ? t("fx.pc_button", "Filtres & recadrage") : t(k, d))} />
                {renderStickerTools()}
                {linkEmbed && (
                  <div data-testid="story-media-link-preview-wrap">
                    <LinkPreviewCard embed={linkEmbed} onRemove={removeLinkEmbed} testId="story-link-preview" compact />
                    <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("story.link_media_hint", "La carte du lien s'affichera en bas de ta story ; un tap l'ouvrira.")}</p>
                  </div>
                )}
              </div>

            {/* Trim window — lot 613 : poignées début / fin, visible pour toute vidéo locale (découpe même sous la limite) */}
            {canTrim && (
              <div className="mx-auto w-full max-w-[340px] space-y-1.5" data-testid="story-trim-ui">
                <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("story.trim_hint2", "Fais glisser les poignées pour choisir le début et la fin (max {n} s).").replace("{n}", String(maxSec))}</p>
                <div className="text-sm font-bold" style={{ color: "var(--yalola-primary)" }} data-testid="story-trim-times">
                  {fmtTime(trimStart)} → {fmtTime(Math.min(trimStart + trimLen, videoDur))}
                  <span className="font-normal text-xs ml-1" style={{ color: "var(--yalola-text-muted)" }}>({Math.round(Math.min(trimLen, videoDur - trimStart))} s)</span>
                </div>
                <VideoTrimTrack videoDur={videoDur} maxSec={maxSec} start={trimStart} len={trimLen} onChange={setTrim} thumbsSrc={config?.settings?.features?.story_trim_thumbnails !== false ? media?.localUrl || null : null} testId="story-trim" />
              </div>
            )}

            {/* Extract mini-loop preview + clip quality (WhatsApp-style confirmation) */}
            {media?.media_type === "video" && media?.localUrl && (
              <div className="mx-auto w-full max-w-[340px] space-y-2.5" data-testid="story-clip-panel">
                <div>
                  <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--yalola-text)" }}>{t("story.extract_preview", "Extrait sélectionné")}</div>
                  <div className="flex items-center gap-3">
                    <div className="relative rounded-lg overflow-hidden border shrink-0" style={{ borderColor: "var(--yalola-border)" }}>
                      <ClipLoop
                        src={media.localUrl}
                        start={clipStart}
                        len={Math.max(0.5, clipEnd - clipStart)}
                        className="h-20 w-32 object-cover bg-black"
                        testId="story-clip-loop"
                      />
                      <span className="absolute bottom-1 left-1 flex items-center gap-1 text-[9px] font-bold text-white px-1 py-[1px] rounded bg-black/60"><Play size={8} /> {t("story.extract_loop", "Aperçu en boucle")}</span>
                    </div>
                    <div className="text-xs min-w-0" style={{ color: "var(--yalola-text-muted)" }}>
                      <div><span className="font-semibold" style={{ color: "var(--yalola-text)" }}>{t("story.frame_start", "Début")}</span> {fmtTime(clipStart)}</div>
                      <div><span className="font-semibold" style={{ color: "var(--yalola-text)" }}>{t("story.frame_end", "Fin")}</span> {fmtTime(clipEnd)}</div>
                    </div>
                  </div>
                </div>
                {willProcess && (
                  <div>
                    <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--yalola-text-muted)" }}>{t("story.clip_quality", "Qualité du clip")}</div>
                    <div className="grid grid-cols-3 gap-1.5" data-testid="story-clip-quality">
                      {[["light", t("story.q_light", "Léger")], ["standard", t("story.q_standard", "Standard")], ["hd", t("story.q_hd", "Haute")]].map(([k, lab]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setClipQuality(k)}
                          className="text-xs rounded-full py-1.5 font-semibold border transition-colors"
                          style={{ borderColor: clipQuality === k ? "var(--yalola-primary)" : "var(--yalola-border)", background: clipQuality === k ? "var(--yalola-primary)" : "transparent", color: clipQuality === k ? "#fff" : "var(--yalola-text)" }}
                          data-testid={`story-clip-q-${k}`}
                        >{lab}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {renderExtras()}
              </div>
              </div>
            )}
          </div>
        )}

        {/* Text story — phone: pinned preview on top, tools scroll under it; PC: tools left, preview right */}
        {mode === "text" && (
          <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
            {previewShell(
            <div
              ref={frameRef}
              className={`${PREVIEW_FRAME} md:max-w-[240px] rounded-2xl overflow-hidden relative touch-none`}
              style={{ ...(bg && bg.startsWith("/verse-bg/") ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: gradientCss(bg) }), containerType: "inline-size" }}
              onPointerDown={(e) => { if (!e.target?.closest?.("[data-sticker]") && !e.target?.closest?.("button")) setSelSticker(null); }}
              data-testid="story-text-preview"
            >
              {bg && bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/30" aria-hidden />}
              {!(editStory?.layout === "free" && !text.trim()) && (stripEmbedUrl(text, linkEmbed) || !linkEmbed) && (
                <div className="absolute inset-0 pointer-events-none">
                  <AutoFitText text={stripEmbedUrl(text, linkEmbed) || t("story.text_placeholder", "Commence à écrire...")} scale={storyFontScale(font, textScale)} refitKey={fontReady} minPx={11} maxRatio={0.12} maxPx={56} boxClassName="p-4" className="relative font-bold text-white" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)", ...storyFontStyle(font) }} testId="story-text-preview-fit" />
                </div>
              )}
              <StoryDrawing url={editStory?.drawing_url} />
              {renderStickerLayer()}
            </div>
            )}
            <div className="space-y-3 md:order-first">
              <Textarea
                autoFocus
                rows={4}
                maxLength={300}
                placeholder={t("story.text_placeholder", "Commence à écrire...")}
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="story-text-input"
              />
              {linkEmbed && (
                <LinkPreviewCard embed={linkEmbed} onRemove={removeLinkEmbed} testId="story-link-preview" compact />
              )}
              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: "var(--yalola-text-muted)" }}>{t("story.backgrounds", "Arrière-plans")}</div>
                <div className="flex flex-wrap gap-2" data-testid="story-bg-palette">
                  {BG_GRADIENTS.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setBg(g.key)}
                      className="h-8 w-8 rounded-full transition-transform hover:scale-110"
                      style={{ background: g.css, boxShadow: bg === g.key ? "0 0 0 2.5px white, 0 0 0 4.5px var(--yalola-primary)" : "none" }}
                      data-testid={`story-bg-${g.key}`}
                    />
                  ))}
                  {BG_IMAGES.map((b) => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => setBg(b.url)}
                      title={b.label}
                      className="h-8 w-8 rounded-full transition-transform hover:scale-110 bg-cover bg-center"
                      style={{ backgroundImage: `url(${b.url})`, boxShadow: bg === b.url ? "0 0 0 2.5px white, 0 0 0 4.5px var(--yalola-primary)" : "none" }}
                      data-testid={`story-bg-${b.key.replace("img:", "img-")}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3" role="radiogroup" aria-label={t("story.font_label", "Police du texte")} data-testid="story-font-row">
                  {STORY_FONTS.map((f) => {
                    const active = font === f.key;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setFont(f.key)}
                        title={f.label}
                        className="h-9 w-9 rounded-full grid place-items-center text-[13px] leading-none transition-transform hover:scale-110"
                        style={{ ...f.style, background: active ? "var(--yalola-primary)" : "var(--yalola-surface-alt)", color: active ? "#fff" : "var(--yalola-text)", boxShadow: active ? "0 0 0 2px white, 0 0 0 3.5px var(--yalola-primary)" : "none" }}
                        data-testid={`story-font-${f.key}`}
                      >
                        Aa
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-3" data-testid="story-text-size">
                  <span className="text-xs font-bold text-white h-6 w-6 rounded-md grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)" }} aria-hidden>Aa</span>
                  <Slider value={[textScale]} min={0.6} max={1.4} step={0.05} onValueChange={([v]) => setTextScale(v)} className="flex-1" aria-label={t("composer.text_size", "Taille du texte")} data-testid="story-text-size-slider" />
                  <span className="text-[11px] tabular-nums w-9 text-right" style={{ color: "var(--yalola-text-muted)" }} data-testid="story-text-size-value">{Math.round(textScale * 100)}%</span>
                </div>
              </div>
              {renderStickerTools()}
              {renderExtras()}
            </div>
          </div>
        )}
        </DialogBody>
        {mode && (
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={discard} disabled={busy} data-testid="story-discard">{t("story.discard", "Abandonner")}</Button>
            <Button
              className="pill-primary rounded-full"
              onClick={publish}
              disabled={busy || (mandatoryPublishAs && !storyPublishAs) || (mode === "media" ? (!!proc || !media || media.status === "error") : !(text.trim() || (editStory?.layout === "free" && (stickers.length > 0 || editStory?.drawing_url))))}
              data-testid="story-publish"
            >
              {proc
                ? `${proc.phase === "trim" ? t("story.trimming", "Découpage") : t("story.uploading_clip", "Envoi")} ${proc.pct}%`
                : busy
                  ? "Publication..."
                  : t("story.share", "Partager dans une story")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
